import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import {
  HelpCircle,
  ChevronDown,
  ChevronUp,
  Home,
  Mail,
  Phone,
  MessageSquare,
  Building2,
  CreditCard,
  Shield,
  Plane,
  Car
} from 'lucide-react';

const FAQ_CATEGORIES = [
  {
    id: 'general',
    nameKey: 'faq.category.general',
    nameFallback: 'General',
    icon: HelpCircle,
    questions: [
      { qKey: 'faq.general.q1', aKey: 'faq.general.a1', q: 'What is TLA Housing Europe?', a: 'TLA Housing Europe is a platform designed specifically for U.S. military personnel and government employees relocating to Europe. We help you find TLA-compliant (Temporary Living Allowance) housing that meets all military requirements, making your PCS move smoother.' },
      { qKey: 'faq.general.q2', aKey: 'faq.general.a2', q: 'Is this service free to use?', a: 'Yes! Searching and browsing properties is completely free. Property owners can list one property for free on our Recon plan. Paid plans offer additional features like more listings, virtual tours, and priority placement.' },
      { qKey: 'faq.general.q3', aKey: 'faq.general.a3', q: 'What countries do you cover?', a: 'We are Germany-first during private preview, with active location coverage expanding as listings and review capacity grow. Additional European countries are on our roadmap—not yet live.' },
      { qKey: 'faq.general.q4', aKey: 'faq.general.a4', q: 'Do I need to be military to use this service?', a: 'While our platform is designed with military families in mind, anyone can use it. However, TLA reimbursement is only available to eligible military members and government employees.' }
    ]
  },
  {
    id: 'tla',
    nameKey: 'faq.category.tla',
    nameFallback: 'TLA & Housing',
    icon: Building2,
    questions: [
      { qKey: 'faq.tla.q1', aKey: 'faq.tla.a1', q: 'What is TLA (Temporary Living Allowance)?', a: "TLA is a daily allowance paid to service members to cover lodging, meals, and incidental expenses while in temporary housing during a PCS move. It's designed to help cover costs while you find permanent housing." },
      { qKey: 'faq.tla.q2', aKey: 'faq.tla.a2', q: 'How do I know if a property is TLA-compliant?', a: 'Listings may show TLA eligibility signals when owners provide them. Always confirm final eligibility with your housing office before committing, as requirements can vary by installation.' },
      { qKey: 'faq.tla.q3', aKey: 'faq.tla.a3', q: 'How long can I stay in TLA housing?', a: 'TLA is typically authorized for up to 10 days upon arrival and 10 days before departure. Extensions may be granted by your housing office based on availability of permanent housing.' },
      { qKey: 'faq.tla.q4', aKey: 'faq.tla.a4', q: 'Can I bring my pets to TLA housing?', a: 'Many of our properties are pet-friendly - look for the pet policy in each listing. However, pet fees and restrictions vary by property. Be sure to discuss this with the property owner before booking.' }
    ]
  },
  {
    id: 'booking',
    nameKey: 'faq.category.booking',
    nameFallback: 'Booking & Payments',
    icon: CreditCard,
    questions: [
      { qKey: 'faq.booking.q1', aKey: 'faq.booking.a1', q: 'How do I book a property?', a: 'Once you find a property you like, click "Contact Owner" or "Request Walkthrough" to connect directly with the property owner. You\'ll discuss availability, terms, and finalize the booking directly with them.' },
      { qKey: 'faq.booking.q2', aKey: 'faq.booking.a2', q: 'Do you handle payments?', a: 'TLA Housing Europe does not process booking payments today. Connect with owners directly and arrange payment off-platform. Secure platform checkout is planned for a later release.' },
      { qKey: 'faq.booking.q3', aKey: 'faq.booking.a3', q: 'Can I cancel my booking?', a: 'Cancellation policies vary by property owner. Be sure to discuss the cancellation terms before confirming your booking. We recommend getting the policy in writing.' },
      { qKey: 'faq.booking.q4', aKey: 'faq.booking.a4', q: 'How do I get reimbursed for TLA?', a: 'Keep all receipts and documentation. Submit your TLA claim through your finance office along with your lodging receipts, orders, and any required forms. Processing times vary by installation.' }
    ]
  },
  {
    id: 'pcs',
    nameKey: 'faq.category.pcs',
    nameFallback: 'PCS & Moving',
    icon: Plane,
    questions: [
      { qKey: 'faq.pcs.q1', aKey: 'faq.pcs.a1', q: 'When should I start looking for housing?', a: 'We recommend starting your housing search 60-90 days before your report date. This gives you time to research areas, connect with sponsors, and secure TLA housing before arrival.' },
      { qKey: 'faq.pcs.q2', aKey: 'faq.pcs.a2', q: 'What is a sponsor and how do they help?', a: 'A sponsor is someone from your gaining unit assigned to help you with your move. They can provide local advice, help with temporary housing, and guide you through in-processing. Contact your new unit to request one.' },
      { qKey: 'faq.pcs.q3', aKey: 'faq.pcs.a3', q: 'How does the PCS Checklist work?', a: "Our PCS Checklist helps you track all the important tasks for your move - from pre-departure paperwork to settling into permanent housing. It saves your progress automatically when you're logged in." },
      { qKey: 'faq.pcs.q4', aKey: 'faq.pcs.a4', q: 'Do you offer rental cars?', a: 'Rental car packages are not available yet. We are evaluating partner options and plan to launch this feature in a future release.' },
    ]
  },
  {
    id: 'owners',
    nameKey: 'faq.category.owners',
    nameFallback: 'For Property Owners',
    icon: Home,
    questions: [
      { qKey: 'faq.owners.q1', aKey: 'faq.owners.a1', q: 'How do I list my property?', a: 'Create a free account, select "Property Owner" as your role, and follow the step-by-step listing process. You\'ll add details, photos, and set your pricing. Our team reviews listings for quality.' },
      { qKey: 'faq.owners.q2', aKey: 'faq.owners.a2', q: 'What are the subscription plans?', a: 'We offer four plans: Recon (free, 1 listing), Basecamp (€29/mo, 3 listings), Command (€79/mo, 10 listings), and Fortress (€199/mo, unlimited). Each tier includes additional features like virtual tours and priority placement.' },
      { qKey: 'faq.owners.q3', aKey: 'faq.owners.a3', q: 'How do I make my property TLA-compliant?', a: 'TLA compliance requirements include having a furnished property with kitchen facilities, utilities included, and meeting safety standards. Contact your local housing office for specific requirements in your area.' },
      { qKey: 'faq.owners.q4', aKey: 'faq.owners.a4', q: 'How do I receive payments?', a: "Payments are handled directly between you and your guests. Most owners accept bank transfers (SEPA in Germany) or other secure payment methods. Always provide receipts for your guests' TLA claims." }
    ]
  },
  {
    id: 'support',
    nameKey: 'faq.category.support',
    nameFallback: 'Support & Safety',
    icon: Shield,
    questions: [
      { qKey: 'faq.support.q1', aKey: 'faq.support.a1', q: 'How do I report a problem with a listing?', a: 'If you encounter any issues with a listing or property owner, please contact us at support@tlahousing.eu. We take all reports seriously and will investigate promptly.' },
      { qKey: 'faq.support.q2', aKey: 'faq.support.a2', q: 'Is my personal information secure?', a: 'Yes, we take data security seriously. We use industry-standard encryption and never share your personal information with third parties without your consent. See our Privacy Policy for details.' },
      { qKey: 'faq.support.q3', aKey: 'faq.support.a3', q: 'How can I contact support?', a: 'You can reach us by email at info@tlahousing.eu, by phone at +49 151 1234 5678, or use our PCS Buddy for instant answers to common questions.' },
      { qKey: 'faq.support.q4', aKey: 'faq.support.a4', q: 'What if I have a dispute with a property owner?', a: "We recommend first trying to resolve issues directly with the property owner. If that doesn't work, contact us at disputes@tlahousing.eu with details and documentation. We'll help mediate when possible." }
    ]
  }
];

function FAQItem({ question, answer, isOpen, onToggle }) {
  return (
    <div className="border-b border-slate-200 last:border-b-0">
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between py-4 text-left hover:bg-slate-50 px-4 -mx-4 transition-colors"
      >
        <span className="font-medium text-slate-700 pr-4">{question}</span>
        {isOpen ? (
          <ChevronUp className="w-5 h-5 text-slate-400 flex-shrink-0" />
        ) : (
          <ChevronDown className="w-5 h-5 text-slate-400 flex-shrink-0" />
        )}
      </button>
      {isOpen && (
        <div className="pb-4 text-slate-600 text-sm leading-relaxed">
          {answer}
        </div>
      )}
    </div>
  );
}

export default function FAQPage() {
  const { t } = useLanguage();
  const [activeCategory, setActiveCategory] = useState('general');
  const [openQuestions, setOpenQuestions] = useState({});

  const toggleQuestion = (categoryId, index) => {
    const key = `${categoryId}-${index}`;
    setOpenQuestions(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const currentCategory = FAQ_CATEGORIES.find(c => c.id === activeCategory);

  return (
    <div className="min-h-screen bg-slate-100 pt-20" data-testid="faq-page">
      {/* Hero */}
      <div className="bg-gradient-to-b from-champagne-100 to-ivory border-b border-champagne-200 py-12">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-signal-blue/10 border border-signal-blue/30 rounded-full mb-4">
            <HelpCircle className="w-4 h-4 text-signal-blue" />
            <span className="text-signal-blue text-sm">{t('faq.hero_badge', 'Support')}</span>
          </div>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold text-slate-900 mb-4 tracking-tight">
            {t('faq.hero_title', 'FREQUENTLY ASKED QUESTIONS')}
          </h1>
          <p className="text-slate-600 max-w-2xl mx-auto">
            {t('faq.hero_subtitle', 'Find answers to common questions about TLA housing, PCS moves, and using our platform.')}
          </p>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-5xl mx-auto px-4 py-12">
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Category Sidebar */}
          <div className="lg:w-64 flex-shrink-0">
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-2 lg:sticky lg:top-24">
              <nav className="space-y-1">
                {FAQ_CATEGORIES.map((category) => (
                  <button
                    key={category.id}
                    onClick={() => setActiveCategory(category.id)}
                    className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors ${
                      activeCategory === category.id
                        ? 'bg-signal-blue/20 text-signal-blue'
                        : 'text-slate-600 hover:bg-slate-100 hover:text-slate-800'
                    }`}
                  >
                    <category.icon className="w-4 h-4" />
                    {t(category.nameKey, category.nameFallback)}
                  </button>
                ))}
              </nav>
            </div>
          </div>

          {/* Questions */}
          <div className="flex-1">
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
              <div className="flex items-center gap-3 mb-6 pb-4 border-b border-slate-200">
                <currentCategory.icon className="w-6 h-6 text-signal-blue" />
                <h2 className="font-heading text-xl font-bold text-slate-800">
                  {t(currentCategory.nameKey, currentCategory.nameFallback)}
                </h2>
              </div>

              <div className="space-y-0">
                {currentCategory.questions.map((item, index) => (
                  <FAQItem
                    key={index}
                    question={t(item.qKey, item.q)}
                    answer={t(item.aKey, item.a)}
                    isOpen={openQuestions[`${activeCategory}-${index}`]}
                    onToggle={() => toggleQuestion(activeCategory, index)}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Contact Section */}
        <div className="mt-12 bg-white rounded-xl shadow-sm border border-slate-200 p-8 text-center">
          <h3 className="font-heading text-xl font-bold text-slate-800 mb-2">
            {t('faq.contact_title', 'Still have questions?')}
          </h3>
          <p className="text-slate-600 mb-6">
            {t('faq.contact_subtitle', 'Our team is here to help. Reach out to us directly.')}
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a
              href="mailto:info@tlahousing.eu"
              className="flex items-center gap-2 px-6 py-3 bg-signal-blue hover:bg-signal-blue-hover text-white rounded-lg transition-colors"
            >
              <Mail className="w-4 h-4" />
              {t('faq.email_support', 'Email Support')}
            </a>
            <a
              href="tel:+4915112345678"
              className="flex items-center gap-2 px-6 py-3 bg-slate-800 hover:bg-slate-700 text-slate-200 rounded-lg transition-colors"
            >
              <Phone className="w-4 h-4" />
              +49 151 1234 5678
            </a>
            <Link
              to="/concierge"
              className="flex items-center gap-2 px-6 py-3 bg-military-gold/20 hover:bg-military-gold/30 text-military-gold rounded-lg transition-colors"
            >
              <MessageSquare className="w-4 h-4" />
              {t('faq.pcs_buddy_link', 'PCS Buddy')}
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
